/**
 * Work-entry icon resolution (ticket 3525, upstream pin e3e7cc3f, MIT),
 * split out of the ported `MessagesTimeline.tsx`. Forge adaptation:
 * presentation icons degrade to glyphs until forge has a favicon/asset
 * service (upstream renders website and native-app images).
 *
 * @module TimelineWorkEntryIcons
 */

import type { WorkLogEntry } from "../../session-logic";
import { toolGroupAction } from "../../workLogToolGroups";
import { resolveWorkEntryToolPresentation } from "../../workLogToolPresentation";
import type { ToolActivityIcon } from "../../workLogToolActivityPresentation";
import {
  BotIcon,
  BrainIcon,
  CheckIcon,
  CircleAlertIcon,
  EyeIcon,
  GlobeIcon,
  HammerIcon,
  MessageCircleIcon,
  SearchIcon,
  SmartphoneIcon,
  SquarePenIcon,
  TerminalIcon,
  WrenchIcon,
  XIcon,
  ZapIcon,
  type LucideIcon,
} from "lucide-react";
import { cn } from "~/lib/utils";

export type TimelineWorkEntry = WorkLogEntry;

export type WorkEntryIconName =
  | "bot"
  | "brain"
  | "browser"
  | "check"
  | "circle-alert"
  | "computer"
  | "device"
  | "eye"
  | "globe"
  | "hammer"
  | "message-circle"
  | "search"
  | "square-pen"
  | "terminal"
  | "pull-request"
  | "t3-code"
  | "wrench"
  | "x"
  | "zap";

const WORK_ENTRY_ICONS: Partial<Record<WorkEntryIconName, LucideIcon>> = {
  bot: BotIcon,
  brain: BrainIcon,
  check: CheckIcon,
  "circle-alert": CircleAlertIcon,
  device: SmartphoneIcon,
  eye: EyeIcon,
  globe: GlobeIcon,
  hammer: HammerIcon,
  "message-circle": MessageCircleIcon,
  search: SearchIcon,
  "square-pen": SquarePenIcon,
  terminal: TerminalIcon,
  wrench: WrenchIcon,
  x: XIcon,
  zap: ZapIcon,
};

export function workToneIcon(tone: TimelineWorkEntry["tone"]): {
  iconName: WorkEntryIconName;
  className: string;
} {
  if (tone === "error") {
    return { iconName: "circle-alert", className: "text-foreground" };
  }
  if (tone === "thinking") {
    return { iconName: "brain", className: "text-icon-muted" };
  }
  if (tone === "info") {
    return { iconName: "check", className: "text-icon-muted" };
  }
  return { iconName: "zap", className: "text-foreground" };
}

/** Forge has no favicon/asset service yet: presentation icons degrade to the glyph. */
export function ToolActivityIconView(props: {
  icon: ToolActivityIcon | undefined;
  fallbackName: WorkEntryIconName;
  className: string;
  muted: boolean;
}) {
  const Icon = WORK_ENTRY_ICONS[props.fallbackName] ?? ZapIcon;
  return <Icon className={cn(props.className, props.muted && "opacity-70")} aria-hidden />;
}

export function toolGroupSummaryIconName(kind: string): WorkEntryIconName {
  switch (kind) {
    case "pull-request":
    case "link-pr":
    case "unlink-pr":
    case "list-prs":
      return "pull-request";
    case "read":
      return "eye";
    case "edit":
      return "square-pen";
    case "command":
      return "terminal";
    case "browser":
      return "browser";
    case "device":
      return "device";
    case "search":
      return "globe";
    case "code-search":
      return "search";
    case "other":
      return "wrench";
    case "dynamic-tool":
      return "hammer";
    case "agent-tool":
      return "bot";
    case "tone-tool":
      return "zap";
    case "update":
    case "mixed":
      return "hammer";
  }
  return "hammer";
}

function entryInput(workEntry: TimelineWorkEntry) {
  return {
    label: workEntry.label,
    ...(workEntry.toolTitle !== undefined ? { toolTitle: workEntry.toolTitle } : {}),
    ...(workEntry.toolData !== undefined ? { toolData: workEntry.toolData } : {}),
    ...(workEntry.toolLifecycleStatus !== undefined
      ? { toolLifecycleStatus: workEntry.toolLifecycleStatus }
      : {}),
  };
}

export function workEntryIconName(workEntry: TimelineWorkEntry): WorkEntryIconName {
  if (
    workEntry.questionAnswer ||
    workEntry.sourceActivityKind === "user-input.requested" ||
    workEntry.sourceActivityKind === "user-input.resolved"
  ) {
    return "message-circle";
  }
  if (workEntry.toolSurface) return workEntry.toolSurface;
  const toolPresentation = resolveWorkEntryToolPresentation(entryInput(workEntry));
  if (toolPresentation) return toolPresentation.icon;
  const action = toolGroupAction(workEntry);
  if (action !== "other") return toolGroupSummaryIconName(action);

  switch (workEntry.itemType) {
    case "mcp_tool_call":
      return "wrench";
    case "dynamic_tool_call":
      return "hammer";
    case "collab_agent_tool_call":
      return "bot";
  }

  // Subagent lifecycle rows (grouped by taskId) get agent identity chrome.
  if (workEntry.taskId) {
    return "bot";
  }

  return workToneIcon(workEntry.tone).iconName;
}
